/**
 * Best Practices Data Composable
 * Fetches the bestPractices GraphQL query once and shares across all widgets.
 */

import { ref, computed } from 'vue'
import { dataFetcher } from '../services/dataFetcher.js'
import {
  WIDGET_TO_BEST_PRACTICE_TYPES,
  BEST_PRACTICE_COPY,
  formatBestPracticeCopy,
  isInsightDismissed
} from '../utils/widgetBestPractices.js'

const BEST_PRACTICES_QUERY = `query bestPractices {
  bestPractices {
    type
    priority
    is_covered
    proof_count
    missed_opportunity
    opportunity_count
    covering_workflow {
      workflow_id
      name
    }
    covering_workflow_recipe {
      name
      recipe_type
    }
  }
}`

// Module-level singleton store
const store = {
  data: ref(null),
  loading: ref(false),
  error: ref(null),
  fetched: false
}

/**
 * URL override for testing: ?bpCovered=N
 * Forces the first N best practices (by priority) to be covered, rest uncovered.
 */
function applyBpCoveredOverride() {
  if (typeof window === 'undefined') return
  const param = new URLSearchParams(window.location.search).get('bpCovered')
  if (param == null || !store.data.value?.length) return
  const coveredCount = parseInt(param, 10)
  if (isNaN(coveredCount)) return
  const sorted = [...store.data.value].sort((a, b) => (b.priority || 0) - (a.priority || 0))
  sorted.forEach((bp, i) => {
    bp.is_covered = i < coveredCount
    if (bp.is_covered) {
      bp.proof_count = bp.proof_count || 5
    }
  })
  store.data.value = sorted
}

/**
 * Fetch best practices from the API (or demo data).
 * No-ops if already fetched unless force=true.
 * @param {Object} [options]
 * @param {boolean} [options.force] - Force re-fetch
 * @param {Function} [options.getDemoData] - Demo data provider function
 */
export async function fetchBestPractices(options = {}) {
  const { force = false, getDemoData = null } = options

  if (store.fetched && !force) return

  if (getDemoData) {
    store.data.value = getDemoData()
    store.fetched = true
    store.loading.value = false
    applyBpCoveredOverride()
    return
  }

  try {
    store.loading.value = true
    store.error.value = null
    const result = await dataFetcher.fetchGraphQL(
      BEST_PRACTICES_QUERY,
      {},
      'bestPractices',
      10, // high priority
      false // don't batch - this is a standalone query
    )
    store.data.value = result?.bestPractices || []
    store.fetched = true
    applyBpCoveredOverride()
  } catch (err) {
    store.error.value = err
    store.data.value = []
  } finally {
    store.loading.value = false
  }
}

/**
 * Returns reactive best practices data from the singleton store.
 * @returns {{ bestPractices: import('vue').ComputedRef, loading: import('vue').Ref, error: import('vue').Ref, refresh: Function }}
 */
export function useBestPracticesData() {
  const bestPractices = computed(() => store.data.value || [])
  const loading = computed(() => store.loading.value || store.data.value === null)
  return {
    bestPractices,
    loading,
    error: store.error,
    refresh: () => fetchBestPractices({ force: true })
  }
}

/**
 * Get best practice objects from the API data for a given widget type.
 * @param {string} widgetType - Widget type (e.g. 'upcoming-tours')
 * @returns {Array} Best practice objects from the API that match this widget's types
 */
export function getBestPracticesForWidget(widgetType) {
  const types = WIDGET_TO_BEST_PRACTICE_TYPES[widgetType]
  if (!types?.length) return []
  const allBPs = store.data.value || []
  return allBPs.filter(bp => types.includes(bp.type))
}

/**
 * Determine the footer state for a widget.
 * Selects the highest-priority best practice to display, preferring missed opportunities.
 *
 * @param {string} widgetType - Widget type
 * @returns {{ bestPractice: Object, state: 'covered'|'missed'|'noCoverage', text: string, detailedText: string }|null}
 */
// URL param override: ?footerState=covered|missed|noCoverage
function getFooterStateOverride() {
  if (typeof window === 'undefined') return null
  return new URLSearchParams(window.location.search).get('footerState')
}

export function getFooterStateForWidget(widgetType) {
  const types = WIDGET_TO_BEST_PRACTICE_TYPES[widgetType]
  if (!types?.length) return null

  const bps = getBestPracticesForWidget(widgetType)
  const override = getFooterStateOverride()

  // When overriding, use the first mapped type's copy with fake data
  if (override && ['covered', 'missed', 'noCoverage'].includes(override)) {
    const bpType = types[0]
    const copy = BEST_PRACTICE_COPY[bpType]
    const fakeBp = bps[0] || { type: bpType, is_covered: override === 'covered', missed_opportunity: override === 'missed', proof_count: 32, opportunity_count: 34, covering_workflow: override === 'covered' ? { workflow_id: 'demo', name: 'Demo' } : null, covering_workflow_recipe: override !== 'covered' ? { name: 'Demo recipe', recipe_type: 'STAY_ON_TOP_OF_ISSUES' } : null, priority: 5 }
    if (override === 'missed') {
      return { bestPractice: { ...fakeBp, is_covered: false, missed_opportunity: true }, state: 'missed', text: formatBestPracticeCopy(copy?.missedShort, fakeBp.opportunity_count, copy?.maxCount), detailedText: formatBestPracticeCopy(copy?.missedDetailed, fakeBp.opportunity_count, copy?.maxCount) }
    }
    if (override === 'covered') {
      return { bestPractice: { ...fakeBp, is_covered: true }, state: 'covered', text: formatBestPracticeCopy(copy?.covered, fakeBp.proof_count, copy?.maxCount), detailedText: '' }
    }
    return { bestPractice: { ...fakeBp, is_covered: false, missed_opportunity: false }, state: 'noCoverage', text: copy?.noCoverage || '', detailedText: '' }
  }

  if (!bps.length) return null

  const byPriority = (a, b) => (b.priority || 0) - (a.priority || 0)

  // 1. Prefer missed opportunity (purple footer)
  const missed = bps.filter(bp => !bp.is_covered && bp.missed_opportunity).sort(byPriority)
  if (missed.length) {
    const bp = missed[0]
    const copy = BEST_PRACTICE_COPY[bp.type]
    return {
      bestPractice: bp,
      state: 'missed',
      text: formatBestPracticeCopy(copy?.missedShort, bp.opportunity_count, copy?.maxCount),
      detailedText: formatBestPracticeCopy(copy?.missedDetailed, bp.opportunity_count, copy?.maxCount)
    }
  }

  // 2. Then covered (green footer)
  const covered = bps.filter(bp => bp.is_covered).sort(byPriority)
  if (covered.length) {
    const bp = covered[0]
    const copy = BEST_PRACTICE_COPY[bp.type]
    const text = (bp.proof_count === 0 && copy?.coveredZero)
      ? copy.coveredZero
      : formatBestPracticeCopy(copy?.covered, bp.proof_count, copy?.maxCount)
    return {
      bestPractice: bp,
      state: 'covered',
      text,
      detailedText: ''
    }
  }

  // 3. Otherwise not covered, no opportunity (grey footer)
  const sorted = [...bps].sort(byPriority)
  const bp = sorted[0]
  const copy = BEST_PRACTICE_COPY[bp.type]
  return {
    bestPractice: bp,
    state: 'noCoverage',
    text: copy?.noCoverage || '',
    detailedText: ''
  }
}

/**
 * Get the footer state for a specific best practice type (by type string).
 * Used when opening the detail panel directly from a best practice row.
 *
 * @param {string} bpType - Best practice type (e.g. 'INVOICE_REMINDER')
 * @returns {{ bestPractice: Object, state: 'covered'|'missed'|'noCoverage', text: string, detailedText: string }|null}
 */
export function getStateForBestPracticeType(bpType) {
  const allBPs = store.data.value || []
  const bp = allBPs.find(b => b.type === bpType)
  if (!bp) return null

  const copy = BEST_PRACTICE_COPY[bpType]
  if (!copy) return null

  if (bp.is_covered) {
    const text = (bp.proof_count === 0 && copy.coveredZero)
      ? copy.coveredZero
      : formatBestPracticeCopy(copy.covered, bp.proof_count, copy.maxCount)
    return {
      bestPractice: bp,
      state: 'covered',
      text,
      detailedText: ''
    }
  }

  if (bp.missed_opportunity) {
    return {
      bestPractice: bp,
      state: 'missed',
      text: formatBestPracticeCopy(copy.missedShort, bp.opportunity_count, copy.maxCount),
      detailedText: formatBestPracticeCopy(copy.missedDetailed, bp.opportunity_count, copy.maxCount)
    }
  }

  return {
    bestPractice: bp,
    state: 'noCoverage',
    text: copy.noCoverage || '',
    detailedText: ''
  }
}

/**
 * Get the insight (detailed missed-opportunity text) for the panel callout.
 * Returns null if no missed opportunity or if dismissed.
 * @param {string} widgetType
 * @returns {{ message: string, bestPractice: Object }|null}
 */
export function getInsightForWidget(widgetType) {
  if (isInsightDismissed(widgetType)) return null

  const bps = getBestPracticesForWidget(widgetType)
  const missed = bps
    .filter(bp => !bp.is_covered && bp.missed_opportunity)
    .sort((a, b) => (b.priority || 0) - (a.priority || 0))

  if (!missed.length) return null

  const bp = missed[0]
  const copy = BEST_PRACTICE_COPY[bp.type]
  return {
    message: formatBestPracticeCopy(copy?.missedDetailed, bp.opportunity_count, copy?.maxCount),
    bestPractice: bp
  }
}
